import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { AuthProvider, ProtectedRoute } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SeasonProvider } from "./contexts/SeasonContext";
import { TTSPlayerProvider } from "./contexts/TTSPlayerContext";
import { Toaster } from "sonner";
import NowPlayingIndicator from "./components/audio/NowPlayingIndicator";

import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import Dashboard from "./pages/Dashboard";
import CommissionerSetup from "./pages/CommissionerSetup";
import AdminDashboard from "./pages/AdminDashboard";
import AdminUserManagement from "./pages/AdminUserManagement";
import DraftLobby from "./pages/DraftLobby";
import ViewTeamsPage from "./pages/ViewTeamsPage";
import DraftRoom from "./pages/DraftRoom";
import SnakeDraftRoom from "./components/snake/SnakeDraftRoom";
import DraftBoardPage from "./pages/DraftBoardPage";
import SnakeDraftBoardPage from "./pages/SnakeDraftBoardPage";
import JoinLeaguePage from "./pages/JoinLeaguePage";
import LeagueHome from "./pages/LeagueHome";
import TeamHQ from "./pages/TeamHQ";
import LineupSet from "./pages/LineupSet";
import MatchupCenter from "./pages/MatchupCenter";
import Standings from "./pages/Standings";
import PlayersBrowser from "./pages/PlayersBrowser";
// [Session C-B CP-4] Relocated league grid - behind the LEAGUES nav tile.
import LeaguesGridView from "./pages/LeaguesGridView";
// [Session C-B CP-5 final fix] See All routes from dashboard tiers.
import NewsListView from "./pages/NewsListView";
import Waivers from "./pages/Waivers";
import Trades from "./pages/Trades";
import LeagueSettings from "./pages/LeagueSettings";
import SimulatorPanel from "./pages/SimulatorPanel";
import PlayoffBracket from "./pages/PlayoffBracket";
import SeasonRecap from "./pages/SeasonRecap";
// [Phase 2d Stripe - Session B] Pricing + billing landing pages.
import PricingPage from "./pages/PricingPage";
import { BillingSuccessPage, BillingCancelPage } from "./pages/BillingPages";
import DiamondSharePage from "./pages/DiamondSharePage";

// Smooth page entry - no italic, just clean motion
const pageTransition = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.20, ease: [0.25, 0.1, 0.25, 1] } },
  exit:    { opacity: 0, y: -6, transition: { duration: 0.12 } },
};

function PageWrap({ children }) {
  return (
    <motion.div variants={pageTransition} initial="initial" animate="animate" exit="exit">
      {children}
    </motion.div>
  );
}

// [Phase 2a.1] Deploy mode is fail-closed: anything other than the literal
// string "public" is treated as admin-test (no public landing, no signup).
const DEPLOY_MODE = process.env.REACT_APP_PGF_DEPLOY_MODE === "public" ? "public" : "admin_test";

function RootRoute() {
  if (DEPLOY_MODE === "admin_test") return <Navigate to="/login" replace />;
  return <PageWrap><LandingPage /></PageWrap>;
}

function AppRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<RootRoute />} />
        {/* [Sprint §Part 4] Public Diamond in the Rough share page -
            no auth required; injects OG meta tags for LinkedIn/X unfurls. */}
        <Route path="/diamond/:date" element={<PageWrap><DiamondSharePage /></PageWrap>} />
        <Route path="/diamond" element={<PageWrap><DiamondSharePage /></PageWrap>} />
        <Route path="/login"  element={<PageWrap><LoginPage /></PageWrap>} />
        <Route path="/signup" element={<PageWrap><SignupPage /></PageWrap>} />
        <Route path="/forgot-password" element={<PageWrap><ForgotPasswordPage /></PageWrap>} />
        <Route path="/reset-password"  element={<PageWrap><ResetPasswordPage /></PageWrap>} />
        <Route path="/join/:inviteCode" element={<PageWrap><JoinLeaguePage /></PageWrap>} />

        <Route path="/dashboard"       element={<ProtectedRoute><PageWrap><Dashboard /></PageWrap></ProtectedRoute>} />
        {/* [Session C-B CP-4] Behind the LEAGUES nav tile. */}
        <Route path="/leagues"         element={<ProtectedRoute><PageWrap><LeaguesGridView /></PageWrap></ProtectedRoute>} />
        {/* [Session C-B CP-4] League-agnostic players view. */}
        <Route path="/players"         element={<ProtectedRoute><PageWrap><PlayersBrowser /></PageWrap></ProtectedRoute>} />
        {/* [Session C-B CP-5 final fix] See All news routes from dashboard tiers. */}
        <Route path="/news/:slug"      element={<ProtectedRoute><PageWrap><NewsListView /></PageWrap></ProtectedRoute>} />
        <Route path="/admin"           element={<ProtectedRoute><PageWrap><AdminDashboard /></PageWrap></ProtectedRoute>} />
        <Route path="/admin/users"     element={<ProtectedRoute><PageWrap><AdminUserManagement /></PageWrap></ProtectedRoute>} />
        <Route path="/league/create"   element={<ProtectedRoute><PageWrap><CommissionerSetup /></PageWrap></ProtectedRoute>} />
        <Route path="/league/:id/setup"  element={<ProtectedRoute><PageWrap><CommissionerSetup /></PageWrap></ProtectedRoute>} />
        <Route path="/league/:id/lobby"  element={<ProtectedRoute><PageWrap><DraftLobby /></PageWrap></ProtectedRoute>} />
        {/* [Item 4] Permanent post-draft record. A draft-only league routes
            here from the dashboard so the CSV is always retrievable. */}
        <Route path="/league/:id/teams"  element={<ProtectedRoute><PageWrap><ViewTeamsPage /></PageWrap></ProtectedRoute>} />
        <Route path="/draft/:id"         element={<ProtectedRoute><DraftRoom /></ProtectedRoute>} />
        <Route path="/draft-snake/:id"   element={<ProtectedRoute><SnakeDraftRoom /></ProtectedRoute>} />
        <Route path="/draft/:id/board"   element={<ProtectedRoute><DraftBoardPage /></ProtectedRoute>} />
        <Route path="/draft-snake/:id/board" element={<ProtectedRoute><SnakeDraftBoardPage /></ProtectedRoute>} />

        {/* Season routes (Phase 1 - Checkpoint 1+2) */}
        {/* [Phase 2c.6-A.1.11 RL-9] DraftRecap page removed. Backend
            /api/season/{id}/draft-recap data endpoint kept (used by team_recaps
            consumers). DraftEndRecapModal replaces the page UX. */}
        <Route path="/league/:id/home"                   element={<ProtectedRoute><PageWrap><LeagueHome /></PageWrap></ProtectedRoute>} />
        <Route path="/league/:id/team/:teamId"           element={<ProtectedRoute><PageWrap><TeamHQ /></PageWrap></ProtectedRoute>} />
        <Route path="/league/:id/team/:teamId/lineup/:week" element={<ProtectedRoute><PageWrap><LineupSet /></PageWrap></ProtectedRoute>} />
        <Route path="/league/:id/matchup/:week"          element={<ProtectedRoute><PageWrap><MatchupCenter /></PageWrap></ProtectedRoute>} />
        <Route path="/league/:id/standings"              element={<ProtectedRoute><PageWrap><Standings /></PageWrap></ProtectedRoute>} />
        <Route path="/league/:id/players"                element={<ProtectedRoute><PageWrap><PlayersBrowser /></PageWrap></ProtectedRoute>} />
        <Route path="/league/:id/waivers"                element={<ProtectedRoute><PageWrap><Waivers /></PageWrap></ProtectedRoute>} />
        <Route path="/league/:id/trades"                 element={<ProtectedRoute><PageWrap><Trades /></PageWrap></ProtectedRoute>} />
        <Route path="/league/:id/settings"               element={<ProtectedRoute><PageWrap><LeagueSettings /></PageWrap></ProtectedRoute>} />
        <Route path="/league/:id/playoffs"               element={<ProtectedRoute><PageWrap><PlayoffBracket /></PageWrap></ProtectedRoute>} />
        <Route path="/league/:id/recap"                  element={<ProtectedRoute><PageWrap><SeasonRecap /></PageWrap></ProtectedRoute>} />

        {/* Dev tools */}
        <Route path="/simulator" element={<ProtectedRoute><PageWrap><SimulatorPanel /></PageWrap></ProtectedRoute>} />

        {/* [Phase 2d Stripe - Session B] Pricing + billing landing pages. */}
        <Route path="/pricing" element={<PageWrap><PricingPage /></PageWrap>} />
        <Route path="/billing/success" element={<ProtectedRoute><BillingSuccessPage /></ProtectedRoute>} />
        <Route path="/billing/cancel"  element={<ProtectedRoute><BillingCancelPage /></ProtectedRoute>} />

        <Route path="*" element={
          <PageWrap>
            <div className="min-h-screen bg-[#111111] flex flex-col items-center justify-center text-center px-4">
              <p className="font-display font-black text-6xl text-[#FF6B00] mb-2">404</p>
              <p className="font-display font-black text-2xl uppercase text-[#F5F5F5] mb-2">That play is not in the playbook.</p>
              <p className="font-body text-[#A0A0A0] mb-6">The page you're looking for doesn't exist.</p>
              <a href="/" className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-display font-black uppercase px-6 py-3 rounded-lg transition-all">Back to Home</a>
            </div>
          </PageWrap>
        } />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <TTSPlayerProvider>
          <SeasonProvider>
            <BrowserRouter>
              <AppRoutes />
              <NowPlayingIndicator />
              {/* [TD-68] Global sonner Toaster mount so Dashboard
                  + DraftLobby toasts (and any future surface that uses
                  the `toast` API) render without per-page Toaster
                  duplication. SnakeDraftRoom's local Toaster remains
                  for legacy continuity. */}
              <Toaster position="top-center" richColors closeButton />
            </BrowserRouter>
          </SeasonProvider>
        </TTSPlayerProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
